import React from "react";
import { Clock3, Star } from "lucide-react";
import { BarChartIcon, User } from "./icons";
import { useNavigate } from "react-router-dom";
import { useGetInstructorSessionStudentsQuery } from "../../services/instructor/instructor";

const AssignedCourseCard = ({ course }) => {
  const { data: sessionStudentsData } = useGetInstructorSessionStudentsQuery(
    course?._id,
    { skip: !course?._id },
  );
  const navigate = useNavigate();

  const studentCount = (sessionStudentsData?.data || []).filter(
    (student) => student?._id,
  ).length;
  const courseLevel = Array.isArray(course?.courseLevel)
    ? course.courseLevel[0]
    : course?.courseLevel;
  const otherInstructorsCount = Math.max(
    (course?.assignedInstructors?.length || 1) - 1,
    0,
  );

  return (
    <div
      onClick={() => navigate(`/instructor/courses/${course._id}`)}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-[#E9EAF0] bg-white shadow-sm transition-shadow hover:shadow-md sm:flex-row lg:flex-col xl:flex-row"
    >
      <div className="h-40 w-full shrink-0 sm:h-auto sm:w-[140px] md:w-[164px] lg:h-40 lg:w-full xl:h-auto xl:w-[164px]">
        <img
          src={course?.courseThumbnail?.secureUrl}
          alt={course?.courseTitle || "Course"}
          className="h-full w-full object-cover"
        />
      </div>

      <div className="min-w-0 flex-1 bg-[#089BC412] px-3 py-3 font-['Inter'] group-hover:bg-[#089BC425] sm:px-4 sm:py-2">
        <div className="mb-1.5 flex items-start justify-between gap-2">
          <span className="max-w-[70%] truncate bg-[#E1F7E3] px-2 py-1 text-[10px] font-medium leading-3 text-[#15711F]">
            {course?.courseCategory}
          </span>

          <div className="flex shrink-0 items-center gap-1 text-xs leading-[22px] tracking-[-1%] text-[#1D2026]">
            <Star size={16} className="fill-[#FD8E1F] text-[#FD8E1F]" />
            <p>
              0 <span className="text-[#8C94A3]">(0)</span>
            </p>
          </div>
        </div>

        <h3 className="mb-3 line-clamp-2 min-h-[44px] break-words text-sm font-medium leading-[22px] tracking-[-1%] text-[#1D2026] sm:text-base">
          {course?.courseTitle}
        </h3>

        <div className="flex flex-col gap-3 border-b border-[#E9EAF0] pb-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-1.5">
            <img
              src="https://randomuser.me/api/portraits/men/32.jpg"
              alt="Instructor"
              className="h-7 w-7 shrink-0 rounded-full border-2 border-white"
            />

            <p className="truncate text-xs text-[#4E5566] font-['Roboto']">
              {otherInstructorsCount > 0
                ? `You and ${otherInstructorsCount} more`
                : "You"}
            </p>
          </div>

          <div className="flex items-center gap-1.5 text-sm tracking-[-1%] text-[#4E5566]">
            <Clock3 size={18} className="shrink-0 text-[#23BD33]" />
            <span className="truncate">{course?.courseAccess}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <div className="flex items-center gap-1.5">
            <User />
            <span className="text-sm font-medium tracking-[-1%] text-[#4E5566]">
              {studentCount}
              <span className="font-normal text-[#8C94A3]"> students</span>
            </span>
          </div>

          <div className="flex items-center gap-1.5 text-sm text-[#4E5566]">
            <BarChartIcon />
            <span className="truncate">{courseLevel}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const AssignedCourses = ({ courses }) => {
  return (
    <div className="mb-6 w-full min-w-0 overflow-hidden bg-white px-3 pb-5 pt-2 shadow-[0_-1px_3.8px_0_#00000040] sm:px-5 sm:pb-6 md:px-7 md:pb-8">
      <h2 className="text-lg font-semibold leading-10 tracking-[-1%] text-[#1D2026] sm:text-xl sm:leading-[48px]">
        My Courses
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2 xl:grid-cols-1 min-[1400px]:grid-cols-2 min-[1400px]:gap-x-8 min-[1400px]:gap-y-6">
        {courses.map((course) => (
          <AssignedCourseCard key={course._id} course={course} />
        ))}
      </div>
    </div>
  );
};

export default AssignedCourses;
